import React, { useEffect, useState } from "react";
import { useTable, useExpanded } from "react-table";
import "./ReactTable.scss";
import {
  Button,
  MenuItem,
  Pagination,
  PaginationItem,
  Select,
} from "@mui/material";

const ReactTable = ({
  columns,
  data,
  renderRowSubComponent,
  manualPagination = false,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  totalCount,
}) => {
  console.log("renderRowSubComponent", renderRowSubComponent);

  // State for client-side pagination
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(10);
  const [gotoPageInput, setGotoPageInput] = useState("");

  // Use either controlled (server) or local (client) state
  const currentPage = manualPagination ? page : clientPage;
  const currentPageSize = manualPagination ? pageSize : clientPageSize;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    toggleRowExpanded,
  } = useTable(
    {
      columns,
      data,
    },
    useExpanded
  );
  const totalItems = manualPagination ? totalCount : rows?.length;
  const totalPages = Math.ceil(totalItems / currentPageSize);

  const getPageItems = () => {
    if (manualPagination) {
      // Server-side: data is already paginated
      return rows;
    } else {
      // Client-side: slice data
      const startIndex = (currentPage - 1) * currentPageSize;
      const endIndex = Math.min(startIndex + currentPageSize, totalItems);
      return rows.slice(startIndex, endIndex);
    }
  };

  const handleGotoPage = () => {
    const pageNumber = parseInt(gotoPageInput, 10);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      if (manualPagination && onPageChange) {
        onPageChange(pageNumber);
      } else {
        setClientPage(pageNumber);
      }
      setGotoPageInput("");
    }
  };

  const handlePageChange = (event, newPage) => {
    if (manualPagination && onPageChange) {
      onPageChange(newPage);
    } else {
      setClientPage(newPage);
    }
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    if (manualPagination && onPageSizeChange) {
      onPageSizeChange(newPageSize);
    } else {
      setClientPageSize(newPageSize);
      setClientPage(1);
    }
  };

  useEffect(() => {
    if (!manualPagination) setClientPage(1);
  }, [data, manualPagination]);

  const formatPageNumber = (page) => {
    return page < 10 ? `0${page}` : page;
  };

  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 650);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="main_table">
      <div className="table_container">
        <table {...getTableProps()} className="table">
          <thead className="table_head">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="head_row">
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    style={column.width ? { width: column.width } : {}}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className={`table_tbody ${totalItems === 0 ? "noDataBody" : ""}`}
          >
            {totalItems === 0 ? (
              <tr>
                <td colSpan={columns.length} className="no-data-message">
                  No data available
                </td>
              </tr>
            ) : (
              getPageItems().map((row, index) => {
                prepareRow(row);
                console.log("row", row);
                return (
                  <>
                    <tr
                      {...row.getRowProps()}
                      className="tbody_row"
                      key={index}
                    >
                      {row.cells.map((cell) => (
                        <td
                          style={
                            cell.column.width
                              ? { width: cell.column.width }
                              : {}
                          }
                          {...cell.getCellProps()}
                          onClick={() =>
                            cell.column.Header === "Action" &&
                            toggleRowExpanded(row.id)
                          }
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {row.isExpanded && renderRowSubComponent ? (
                      <tr className="tbody_row">
                        <td colSpan={columns.length}>
                          {renderRowSubComponent({ row })}
                        </td>
                      </tr>
                    ) : null}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 0 && (
        <div className="paginationComponent">
          <div className="pagination_container">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              boundaryCount={2}
              siblingCount={1}
              variant="outlined"
              shape="rounded"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  components={{
                    previous: () => (
                      <Button
                        variant="outlined"
                        onClick={() => handlePageChange(null, currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        {isSmallScreen ? `<` : `< Previous`}
                      </Button>
                    ),
                    next: () => (
                      <Button
                        variant="outlined"
                        onClick={() => handlePageChange(null, currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        {isSmallScreen ? `>` : `Next >`}
                      </Button>
                    ),
                  }}
                  // Format page label with leading zero
                  page={formatPageNumber(item.page)}
                />
              )}
            />
          </div>
          <div className="pageSizeSelectorComponent">
            <div className="goto-page-input">
              <input
                type="number"
                placeholder="Go to Page"
                value={gotoPageInput}
                onChange={(e) => setGotoPageInput(e.target.value)}
              />
              <button className="goToButton" onClick={handleGotoPage}>
                Go
              </button>
            </div>
            <Select
              className="pageSizeSelector"
              value={currentPageSize}
              onChange={handlePageSizeChange}
              displayEmpty
              inputProps={{ "aria-label": "Page Size Selector" }}
              sx={{
                ".MuiSelect-icon": {
                  color: "white", // Makes the SVG icon white
                },
              }}
            >
              <MenuItem value={10}>10 / page</MenuItem>
              <MenuItem value={20}>20 / page</MenuItem>
              <MenuItem value={30}>30 / page</MenuItem>
              <MenuItem value={50}>50 / page</MenuItem>
              <MenuItem value={100}>100 / page</MenuItem>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReactTable;
